import type { NumberFormatValues } from 'react-number-format';
import { NumericFormat } from 'react-number-format';
import { MDText } from 'i18n-react';

import { Button } from '@motorway/mw-highway-code';
import { Input } from '@motorway/mw-highway-code/alpha';
import { INTENT } from '@motorway/mw-highway-code/enums';

import cypressIds from 'Components/cypress_ids.json';
import { applyCypressData } from 'Utilities/helpers';

import { BID_ERROR_MESSAGE_ID, BID_INPUT_ID, MAX_BID_VALUE } from '../ProxyBidActions.const';
import LocalTexts from '../ProxyBidActions.texts';
import type { BidInputFieldProps } from '../ProxyBidActions.types';
import { ProxyBidLabel } from '../ProxyBidLabel/ProxyBidLabel';

import styles from './ProxyBidInputField.module.scss';

const LocalT = new MDText(LocalTexts);

const numericFormatIsAllowed = (numberFormatValue: NumberFormatValues): boolean => {
	const { floatValue } = numberFormatValue;

	if (typeof floatValue !== 'number') return true;

	if (floatValue === 0) return false;

	return floatValue <= MAX_BID_VALUE;
};

const numericFormatProps = {
	allowNegative: false,
	customInput: Input,
	decimalScale: 0,
	isAllowed: numericFormatIsAllowed,
	thousandSeparator: ',',
};

const thcInputProps = {
	autoComplete: 'off',
	fullWidth: true,
	id: BID_INPUT_ID,
	label: null,
	prepend: '£',
	showLabel: false,
};

export const ProxyBidInputField: React.FC<BidInputFieldProps> = ({ input, meta, priceReserve }) => {
	const { dirtySinceLastSubmit, submitError, submitSucceeded } = meta;

	const shouldShowErrorIntent = submitError && !dirtySinceLastSubmit;
	const shouldShowSuccessIntent = submitSucceeded && !dirtySinceLastSubmit;

	return (
		<>
			<ProxyBidLabel errorMessage={submitError} />
			<div className={styles.inputAndButtonContainer}>
				<div className={styles.inputContainerForPopover}>
					<NumericFormat
						aria-describedby={submitError ? BID_ERROR_MESSAGE_ID : undefined}
						intent={
							(shouldShowErrorIntent ? INTENT.ERROR : undefined) ||
							(shouldShowSuccessIntent ? INTENT.SUCCESS : undefined)
						}
						placeholder={priceReserve.displayValue.replace('£', '')}
						{...thcInputProps}
						{...numericFormatProps}
						{...input}
						type="text"
					/>
				</div>
				<Button
					{...applyCypressData(cypressIds.buttons.submit)}
					fullWidth={true}
					label={`${LocalT.translate('bidButton.desktop')}`}
					type="submit"
					variant="primary"
				/>
			</div>
		</>
	);
};
